import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

import { t, type MessageKey } from '../i18n';
import { Button } from './button';

export interface TabBarItem {
  value: string | number;
  label?: string;
  labelKey?: MessageKey;
}

interface TabBarProps {
  className?: string;
  items: TabBarItem[];
  orientation?: 'horizontal' | 'vertical';
  value: string | number;
  onChange: (value: string) => void;
}

export const TabBar = ({ className = '', items, orientation = 'horizontal', value, onChange }: TabBarProps) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ height: 0, offset: 0, width: 0 });
  const [animationEnabled, setAnimationEnabled] = useState(false);
  const activeValue = String(value);

  useLayoutEffect(() => {
    let nextFrame = 0;
    const frame = requestAnimationFrame(() => {
      nextFrame = requestAnimationFrame(() => setAnimationEnabled(true));
    });
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(nextFrame);
    };
  }, []);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    const update = () => {
      const active = host.querySelector<HTMLElement>('.tab-bar-item.active');
      setIndicator(
        active
          ? {
              height: active.offsetHeight,
              offset: orientation === 'vertical' ? active.offsetTop : active.offsetLeft,
              width: active.offsetWidth,
            }
          : { height: 0, offset: 0, width: 0 },
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(host);
    return () => observer.disconnect();
  }, [activeValue, items, orientation]);

  return (
    <div
      className={`tab-bar ${indicator.width && indicator.height ? 'has-active' : ''} ${
        animationEnabled ? 'is-animated' : ''
      } ${className}`.trim()}
      role="tablist"
      aria-orientation={orientation}
      data-orientation={orientation}
      ref={hostRef}
      style={
        {
          '--tab-indicator-height': `${indicator.height}px`,
          '--tab-indicator-width': `${indicator.width}px`,
          '--tab-indicator-offset': `${indicator.offset}px`,
        } as CSSProperties
      }
    >
      {items.map((item) => {
        const itemValue = String(item.value);
        const active = itemValue === activeValue;
        return (
          <Button
            variant="plain"
            className={`tab-bar-item${active ? ' active' : ''}`}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(itemValue)}
            key={itemValue}
          >
            {item.labelKey ? t(item.labelKey) : item.label}
          </Button>
        );
      })}
    </div>
  );
};
